import type {
  SopReplayRunningStep,
  SopReplayStepView,
  ToolCallRunResponse,
} from '@erp/rpc';

// ---------------------------------------------------------------------------
// How the frontend READS a procedure replay. One definition of each rule, for every surface.
//
// Three renderers show the same replayed step — the span tree (`components/agent-trace`), the
// message thread (`agentTraceGrouping` → `AgentSessionViewer`) and the operator's card list
// (`SopReplayViewer`) — and their layouts genuinely differ. What must NOT differ is what a step
// IS: which procedure it belongs to, whether its post-condition held, what its mode is called.
// Each of those had grown three copies, and they had already drifted: one verify derivation
// ignored `ran` and reported a check that never executed as failed, and one running-step rule
// matched on index where the others used the full step key.
//
// The rules live here; the folds stay where they are as projections over them.
// ---------------------------------------------------------------------------

/** Rows the backend writes once per replayed step, carrying the header data (name, mode, verify
 * grade) that the action rows can't supply. Must match `SOP_REPLAY_STEP_TOOL_TYPE` in
 * backend/src/core/services/operator/sopReplayStepTrace.ts. */
export const SOP_REPLAY_STEP_TOOL_TYPE = 'agent.sop_replay.step';

// --- Reading the backend's tags ---------------------------------------------

/** The tag keys the backend stamps on every row a replay produces — on the live session event and
 * on the persisted row's `inputData` alike, which is what lets one reader serve both. */
export interface SopReplayTag {
  stepId: string | null;
  stepIndex: number | null;
  procedureName: string | null;
  procedureOrdinal: number;
}

function str(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function optNum(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

/**
 * The replay tag on a metadata/inputData bag, or null when the row has nothing to do with a
 * replay — which is every row of an ordinary agentic run.
 *
 * All fields are optional on the wire: a run with no procedure in it carries none, and a legacy
 * run may carry only some.
 */
export function readSopTag(
  bag: Record<string, unknown> | undefined,
): SopReplayTag | null {
  if (!bag) return null;
  const stepId = str(bag.sopStepId);
  const stepIndex = optNum(bag.sopStepIndex);
  // Neither key present → not part of a replay at all.
  if (stepId === null && stepIndex === null) return null;
  return {
    stepId,
    stepIndex,
    procedureName: str(bag.sopProcedureName),
    procedureOrdinal: optNum(bag.sopProcedureOrdinal) ?? 1,
  };
}

// --- Identity ---------------------------------------------------------------

/**
 * Identity of the procedure a tag/step belongs to — the ordinal ALONE.
 *
 * The name is deliberately excluded: it is display text, and the two sides of a group can
 * legitimately disagree about it. On the `operator.run_sop` path the wire boundary carries no
 * procedure at all (only the operator's inline `run_procedure` stamps one), so the live
 * `sopReplay.steps` arrive with `procedure: null` while the persisted rows are tagged with Node's
 * `fallbackProcedureName` — the SOP title, which only Node knows. Keying on the name splits that
 * one replay in two: an unnamed live group beside a named persisted one.
 *
 * The ordinal is sufficient for identity: it counts replays within the run
 * (`RunProcedureTool._replays`), so two replays of the SAME procedure stay distinct and two
 * DIFFERENT procedures never collide.
 */
export function procedureKey(ordinal: number): string {
  return String(ordinal);
}

/** A step's identity within its procedure: the stable row id when there is one, else the ordinal
 * position (the legacy markdown-only replay path emits no stepId). */
export function stepKey(
  stepId: string | null | undefined,
  index: number | null | undefined,
): string {
  return stepId ? `id:${stepId}` : `ix:${index ?? ''}`;
}

/** Does this tagged row belong to this step? The tag-side spelling of `stepKey` equality. */
export function tagMatchesStep(
  bag: Record<string, unknown> | undefined,
  step: Pick<SopReplayStepView, 'index' | 'stepId'>,
): boolean {
  const tag = readSopTag(bag);
  if (!tag) return false;
  return (
    stepKey(tag.stepId, tag.stepIndex) === stepKey(step.stepId, step.index)
  );
}

// --- Step view-model constructors -------------------------------------------

/**
 * Rebuild a completed run's step headers from its persisted boundary rows.
 *
 * The live view gets these from `activity.sopReplay.steps`; a finished run has no live session, so
 * they come from the timeline the run page already fetches. Same shape either way, so both feed
 * the same folds.
 */
export function sopStepsFromToolCalls(
  toolCalls: Pick<
    ToolCallRunResponse,
    'toolType' | 'inputData' | 'outputData'
  >[],
): SopReplayStepView[] {
  const steps: SopReplayStepView[] = [];
  for (const toolCall of toolCalls) {
    if (toolCall.toolType !== SOP_REPLAY_STEP_TOOL_TYPE) continue;
    const data = toolCall.inputData;
    const index = optNum(data.sopStepIndex);
    if (index === null) continue;
    steps.push({
      index,
      name: str(data.sopStepName) ?? '',
      mode: readMode(data.sopStepMode),
      codeKind: readCodeKind(data.sopStepCodeKind),
      stepId: str(data.sopStepId),
      // Why the step couldn't be reproduced. It rides `outputData`, not the tag bag — the live
      // view reads it off the wire boundary, so without it here a failed step's reason survived
      // right up until the page was refreshed and then read as a bare red badge.
      error: readError(toolCall.outputData),
      verify: readVerify(data.sopStepVerify),
      // A boundary row records no-op-ness on the step itself; absent on rows written before it.
      noop: data.sopStepNoop === true,
      procedure: readProcedure(data),
      owner: str(data.sopOwnerName),
    });
  }
  return steps;
}

/** The failure reason off a persisted boundary row's `outputData`. Null for every step that
 * didn't fail — and for a legacy row written before the field existed. */
function readError(
  outputData: Record<string, unknown> | null | undefined,
): string | null {
  const error = outputData?.error;
  return typeof error === 'string' && error.trim() ? error : null;
}

function readMode(raw: unknown): SopReplayStepView['mode'] {
  return raw === 'healed' || raw === 'agentic' || raw === 'failed'
    ? raw
    : 'deterministic';
}

function readCodeKind(raw: unknown): SopReplayStepView['codeKind'] {
  return raw === 'ref' || raw === 'coordinate' ? raw : null;
}

function readVerify(raw: unknown): SopReplayStepView['verify'] {
  if (typeof raw !== 'object' || raw === null) return null;
  const grade = raw as Record<string, unknown>;
  const kind = grade.kind;
  // `passed` is nullable in the persisted grade but not in the view, so it narrows to false here.
  // `ran` is carried through faithfully — it's what tells the renderer that a declared
  // post-condition never executed, which is NOT the same as one that failed.
  return {
    kind: kind === 'url_contains' || kind === 'text_visible' ? kind : null,
    ran: grade.ran === true,
    passed: grade.passed === true,
  };
}

function readProcedure(
  data: Record<string, unknown>,
): SopReplayStepView['procedure'] {
  const name = str(data.sopProcedureName);
  if (name === null) return null;
  return { name, ordinal: optNum(data.sopProcedureOrdinal) ?? 1 };
}

/**
 * The step to draw as in-progress: the announced running step, unless its completion already
 * landed in `steps` — the two signals ride the same stream, and a snapshot/delta race can briefly
 * present both. A resolved step always wins.
 */
export function visibleRunningStep(
  steps: SopReplayStepView[],
  running: SopReplayRunningStep | null | undefined,
): SopReplayRunningStep | null {
  if (!running) return null;
  const key = stepKey(running.stepId, running.index);
  return steps.some((s) => stepKey(s.stepId, s.index) === key) ? null : running;
}

// --- Presentation ------------------------------------------------------------

/**
 * The step's Verify post-condition state (AI-340), or null when there is no machine verdict to
 * show — the step declared no post-condition, or declared one that never executed. Both mean
 * success rests on the code/agent self-claim, so drawing a red ✗ for the second would report a
 * failure that never happened.
 */
export function sopVerifyState(
  verify: SopReplayStepView['verify'],
): 'held' | 'failed' | null {
  if (!verify?.kind || !verify.ran) return null;
  return verify.passed ? 'held' : 'failed';
}

/**
 * The badge text for a resolved step.
 *
 * An optional (`best_effort`) step whose target was absent is reported as `deterministic` with
 * `noop` set — technically true, and actively misleading in a login shakeout, where "did the MFA
 * step actually fill anything?" is the question. It reads as `skipped`.
 */
export function stepModeLabel(
  step: Pick<SopReplayStepView, 'mode' | 'noop'>,
): string {
  return step.noop ? 'skipped' : step.mode;
}

/** How the step's code was captured: by element reference, or by screen coordinate. */
export function codeKindLabel(
  codeKind: SopReplayStepView['codeKind'],
): string | null {
  if (codeKind === 'ref') return '⚡ref';
  if (codeKind === 'coordinate') return '⚡canvas';
  return null;
}

/**
 * One palette in two representations — shadcn Badge variants and raw Tailwind classes — because
 * some surfaces render a `<Badge>` and others a bare span. Co-located rather than derived: the two
 * are different type universes, and adjacency is what stops them drifting. Before this they were
 * four separate maps that referred to each other by comment.
 */
export const SOP_MODE_BADGE_VARIANT: Record<
  SopReplayStepView['mode'],
  'success' | 'warning' | 'secondary' | 'destructive'
> = {
  deterministic: 'success',
  healed: 'warning',
  agentic: 'secondary',
  failed: 'destructive',
};

export const SOP_MODE_CLASS: Record<SopReplayStepView['mode'], string> = {
  deterministic: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  healed: 'border-amber-200 bg-amber-50 text-amber-700',
  agentic: 'border-stone-200 bg-stone-50 text-stone-600',
  failed: 'border-red-200 bg-red-50 text-red-700',
};

/** The mode classes for a possibly-unknown mode string (a span carries `sopStepMode` as a plain
 * string, since it round-tripped through the tag bag). Falls back to the deterministic palette. */
export function sopModeClass(mode: string | null | undefined): string {
  return (
    SOP_MODE_CLASS[mode as SopReplayStepView['mode']] ??
    SOP_MODE_CLASS.deterministic
  );
}
